/**
 * VideoPage - Image-to-Video Generation
 * Generacja video z obrazów modelki przez RunPod wan-2-1-i2v-720
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  TextField,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  PlayCircle as PlayIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import AppHeader from '../components/common/AppHeader';
import { PoseSelector } from '../components/video/PoseSelector';
import { AdvancedOptions } from '../components/video/AdvancedOptions';
import {
  getVideoPoses,
  generateVideo,
  waitForVideoCompletion,
  VideoPose,
  VideoConfig,
  VideoStatus,
} from '../api/video';
import './VideoPage.css';

export const VideoPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // State
  const [config, setConfig] = useState<VideoConfig | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [selectedPoseId, setSelectedPoseId] = useState<string | undefined>();
  const [userPrompt, setUserPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generationStatus, setGenerationStatus] = useState<string | null>(null);
  const [result, setResult] = useState<VideoStatus | null>(null);

  // Advanced options
  const [advancedParams, setAdvancedParams] = useState({
    duration: 5,
    size: '1280*720',
    num_inference_steps: 40,
    guidance: 5,
    flow_shift: 5,
    seed: -1,
    enable_safety_checker: false,
  });

  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const videoConfig = await getVideoPoses();
      setConfig(videoConfig);

      // Set default advanced params
      if (videoConfig.default_params) {
        setAdvancedParams({
          duration: videoConfig.default_params.duration || 5,
          size: videoConfig.default_params.size || '1280*720',
          num_inference_steps: videoConfig.default_params.num_inference_steps || 40,
          guidance: videoConfig.default_params.guidance || 5,
          flow_shift: videoConfig.default_params.flow_shift || 5,
          seed: -1,
          enable_safety_checker: videoConfig.default_params.enable_safety_checker || false,
        });
      }
    } catch (err: any) {
      console.error('Failed to load video config:', err);
      setError('Failed to load configuration');
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      setImagePreviewUrl(URL.createObjectURL(file));
      setError(null);
    }
  };

  const handleGenerate = async () => {
    if (!selectedImage) {
      setError(t('video.error.noImage'));
      return;
    }

    if (!selectedPoseId && !userPrompt) {
      setError(t('video.error.noPose'));
      return;
    }

    setLoading(true);
    setError(null);
    setGenerationStatus('Uploading image...');
    setResult(null);

    try {
      setGenerationStatus('Starting video generation...');

      // Start generation with file upload
      const response = await generateVideo({
        image_file: selectedImage,  // Send File object directly
        pose_id: selectedPoseId,
        user_prompt: userPrompt,
        advanced: advancedParams,
      });

      if (!response.success || !response.job_id) {
        throw new Error(response.message || 'Failed to start generation');
      }

      setGenerationStatus(`Video generation in progress (Job: ${response.job_id})...`);

      // Wait for completion
      const finalStatus = await waitForVideoCompletion(
        response.job_id,
        (status) => {
          // Update progress
          setGenerationStatus(
            `Status: ${status.status}${status.execution_time ? ` (${Math.round(status.execution_time)}s)` : ''}`
          );
        },
        5000, // Poll every 5 seconds
        600000 // Max 10 minutes
      );

      if (finalStatus.status === 'COMPLETED' && finalStatus.output_url) {
        setResult(finalStatus);
        setGenerationStatus('Video generation completed!');
      } else if (finalStatus.status === 'FAILED') {
        throw new Error(finalStatus.error || 'Video generation failed');
      } else {
        throw new Error(`Unexpected status: ${finalStatus.status}`);
      }
    } catch (err: any) {
      console.error('Video generation error:', err);
      setError(err.message || 'An error occurred');
      setGenerationStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAdvancedChange = (field: string, value: any) => {
    setAdvancedParams((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  if (!config) {
    return (
      <Box>
        <AppHeader />
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      <AppHeader />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          {/* Header */}
          <Typography variant="h4" gutterBottom>
            🎬 {t('video.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('video.subtitle')}
          </Typography>

          {!selectedImage && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>Step 1:</strong> {t('video.step1.hint')}
            </Alert>
          )}

          {selectedImage && !selectedPoseId && !userPrompt && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>Step 2:</strong> {t('video.step2.hint')}
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Image Upload */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('video.step1')}
            </Typography>

            <Box
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main' },
              }}
            >
              <input
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                style={{ display: 'none' }}
                id="video-image-upload"
              />
              <label htmlFor="video-image-upload" style={{ cursor: 'pointer' }}>
                {imagePreviewUrl ? (
                  <Box>
                    <img
                      src={imagePreviewUrl}
                      alt="Selected"
                      style={{ maxWidth: '300px', maxHeight: '400px', borderRadius: '8px' }}
                    />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      {selectedImage?.name}
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <UploadIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      {t('video.step1.upload')}
                    </Typography>
                  </Box>
                )}
              </label>
            </Box>
          </Box>

          {/* Pose Selector */}
          {config.poses && config.poses.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('video.step2')}
              </Typography>
              <PoseSelector
                poses={config.poses}
                selectedPoseId={selectedPoseId}
                onSelectPose={setSelectedPoseId}
                isAdmin={user?.role === 'admin'}
              />
            </Box>
          )}

          {/* Custom Prompt */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('video.step3')}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder={t('video.step3.placeholder')}
              value={userPrompt}
              onChange={(e) => setUserPrompt(e.target.value)}
              variant="outlined"
            />
          </Box>

          {/* Advanced Options */}
          <AdvancedOptions
            sizeOptions={config.size_options || ['1280*720', '720*1280']}
            durationOptions={config.duration_options || [5, 10]}
            defaultParams={config.default_params}
            values={advancedParams}
            onChange={handleAdvancedChange}
          />

          {/* Generate Button */}
          <Box sx={{ mt: 3 }}>
            {selectedImage && (selectedPoseId || userPrompt) && !loading && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <strong>{t('video.ready.title')}</strong> {t('video.ready.message')}
              </Alert>
            )}
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleGenerate}
              disabled={loading || !selectedImage || (!selectedPoseId && !userPrompt)}
              startIcon={loading ? <CircularProgress size={20} /> : <PlayIcon />}
            >
              {loading ? t('video.generating') : t('video.generate')}
            </Button>
            {!selectedImage && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                {t('video.upload.enable')}
              </Typography>
            )}
            {selectedImage && !selectedPoseId && !userPrompt && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                {t('video.pose.enable')}
              </Typography>
            )}
          </Box>

          {/* Generation Status */}
          {generationStatus && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {generationStatus}
              </Typography>
              {loading && <LinearProgress sx={{ mt: 1 }} />}
            </Box>
          )}

          {/* Result */}
          {result && result.output_url && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                {t('video.result')}
              </Typography>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <video
                  src={result.output_url}
                  controls
                  style={{ width: '100%', maxWidth: '800px', borderRadius: '8px' }}
                />

                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    href={result.output_url}
                    download
                  >
                    {t('video.download')}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default VideoPage;
